import Phaser from "phaser";
import { GameRuntime } from "@/game/runtime";
import { POWERUP_LIST } from "@/shared/data/powerups";
import { useGameStore } from "@/shared/state/gameStore";
import { ANIMATIONS } from "@/shared/data/animations";
import { assetUrl } from "@/shared/utils/assets";

const MAIN_MUSIC_VOLUME = 0.4;

const AUDIO_KEYS = {
  musicLofi: "music-lofi",
  like: "sfx-like",
  chatReceived: "sfx-chat-received",
  badChatReceived: "sfx-badchat-received",
  badChatRemoved: "sfx-badchat-removed",
  badChatExpired: "sfx-badchat-expired",
  powerup1: "sfx-powerup-1",
  powerup2: "sfx-powerup-2",
  powerup3: "sfx-powerup-3",
  uwu: "sfx-uwu",
  backflip: "anim-backflip",
  auraFarmer: "anim-aura-farmer",
} as const;

const POWERUP_PURCHASE_SOUND_KEYS: readonly string[] = [
  AUDIO_KEYS.powerup1,
  AUDIO_KEYS.powerup2,
  AUDIO_KEYS.powerup3,
];

const POWERUP_PURCHASE_SOUND_BY_ID: Partial<Record<string, string>> = {
  soft_blush: AUDIO_KEYS.uwu,
};

const ANIMATION_SOUND_KEY_BY_SRC: Partial<Record<string, string>> = {
  "/audio/backflip.mp3": AUDIO_KEYS.backflip,
  "/audio/aura-farming.mp3": AUDIO_KEYS.auraFarmer,
};

const ANIMATION_SOUND_BY_AVATAR = Object.values(ANIMATIONS).reduce<
  Partial<
    Record<
      (typeof ANIMATIONS)[keyof typeof ANIMATIONS]["avatarAnimation"],
      {
        key: string;
        duckVolume: number;
        durationMs: number;
      }
    >
  >
>((accumulator, definition) => {
  const soundEffect = definition.soundEffect;
  if (!soundEffect) {
    return accumulator;
  }

  const key = ANIMATION_SOUND_KEY_BY_SRC[soundEffect.src];
  if (!key) {
    return accumulator;
  }

  accumulator[definition.avatarAnimation] = {
    key,
    duckVolume: soundEffect.duckVolume ?? 0.16,
    durationMs: soundEffect.durationMs,
  };
  return accumulator;
}, {});

type VolumeControlledSound = Phaser.Sound.BaseSound & {
  setVolume: (volume: number) => VolumeControlledSound;
};

export const preloadAudioAssets = (scene: Phaser.Scene) => {
  scene.load.audio(AUDIO_KEYS.musicLofi, assetUrl("audio/lofi.mp3"));
  scene.load.audio(AUDIO_KEYS.like, assetUrl("audio/sfx/like.mp3"));
  scene.load.audio(
    AUDIO_KEYS.chatReceived,
    assetUrl("audio/sfx/chat_received.mp3"),
  );
  scene.load.audio(
    AUDIO_KEYS.badChatReceived,
    assetUrl("audio/sfx/badchat_received.mp3"),
  );
  scene.load.audio(
    AUDIO_KEYS.badChatRemoved,
    assetUrl("audio/sfx/badchat_removed.mp3"),
  );
  scene.load.audio(AUDIO_KEYS.badChatExpired, assetUrl("audio/sfx/badchat.mp3"));
  scene.load.audio(AUDIO_KEYS.powerup1, assetUrl("audio/sfx/powerup1.mp3"));
  scene.load.audio(AUDIO_KEYS.powerup2, assetUrl("audio/sfx/powerup2.mp3"));
  scene.load.audio(AUDIO_KEYS.powerup3, assetUrl("audio/sfx/powerup3.mp3"));
  scene.load.audio(AUDIO_KEYS.uwu, assetUrl("audio/sfx/uwu.mp3"));
  scene.load.audio(AUDIO_KEYS.backflip, assetUrl("audio/backflip.mp3"));
  scene.load.audio(AUDIO_KEYS.auraFarmer, assetUrl("audio/aura-farming.mp3"));
};

export class AudioSystem {
  private lofi?: Phaser.Sound.BaseSound;
  private unsubscribeStore?: () => void;
  private unsubscribeLikeClicked?: () => void;
  private unsubscribePowerupPurchased?: () => void;
  private unsubscribeChatSpawned?: () => void;
  private unsubscribeChatModerated?: () => void;
  private unsubscribeChatExpired?: () => void;
  private unsubscribeAnimationPlayed?: () => void;
  private restoreMusicTimer?: Phaser.Time.TimerEvent;
  private lastPowerupSoundKey: string | null = null;

  constructor(
    private scene: Phaser.Scene,
    private runtime: GameRuntime,
  ) {}

  start() {
    this.lofi = this.scene.sound.add(AUDIO_KEYS.musicLofi, {
      loop: true,
      volume: MAIN_MUSIC_VOLUME,
    });

    this.unsubscribeStore = useGameStore.subscribe((state) => {
      this.syncMusic(state.unlocks.music.lofi);
    });
    this.syncMusic(useGameStore.getState().unlocks.music.lofi);

    this.unsubscribeLikeClicked = this.runtime.on("likeClicked", () => {
      this.playOneShot(AUDIO_KEYS.like, this.randomLikeVolume());
    });

    this.unsubscribePowerupPurchased = this.runtime.on(
      "powerupPurchased",
      ({ powerupId }) => {
        const definition = POWERUP_LIST.find((powerup) => powerup.id === powerupId);
        if (!definition) {
          return;
        }

        const soundKey =
          POWERUP_PURCHASE_SOUND_BY_ID[powerupId] ??
          this.getNextGenericPowerupSoundKey();

        this.playOneShot(soundKey, 0.55);
      },
    );

    this.unsubscribeChatSpawned = this.runtime.on(
      "chatMessageSpawned",
      ({ message }) => {
        if (message.kind === "bad") {
          this.playOneShot(AUDIO_KEYS.badChatReceived, 0.52);
          return;
        }

        if (message.kind === "nice" || message.kind === "morale") {
          this.playOneShot(AUDIO_KEYS.chatReceived, 0.42);
        }
      },
    );

    this.unsubscribeChatModerated = this.runtime.on(
      "chatMessageModerated",
      ({ message }) => {
        if (message.kind === "bad") {
          this.playOneShot(AUDIO_KEYS.badChatRemoved, 0.52);
        }
      },
    );

    this.unsubscribeChatExpired = this.runtime.on(
      "chatMessageExpired",
      ({ message }) => {
        if (message.kind === "bad") {
          this.playOneShot(AUDIO_KEYS.badChatExpired, 0.56);
        }
      },
    );

    this.unsubscribeAnimationPlayed = this.runtime.on(
      "animationPlayed",
      ({ animationId }) => {
        const sound = ANIMATION_SOUND_BY_AVATAR[animationId];
        if (!sound) {
          return;
        }

        this.duckMusic(sound.duckVolume, sound.durationMs);
        this.playOneShot(sound.key, 1);
      },
    );
  }

  destroy() {
    this.unsubscribeStore?.();
    this.unsubscribeLikeClicked?.();
    this.unsubscribePowerupPurchased?.();
    this.unsubscribeChatSpawned?.();
    this.unsubscribeChatModerated?.();
    this.unsubscribeChatExpired?.();
    this.unsubscribeAnimationPlayed?.();

    this.restoreMusicTimer?.remove(false);
    this.restoreMusicTimer = undefined;

    if (this.lofi) {
      this.lofi.stop();
      this.lofi.destroy();
      this.lofi = undefined;
    }
  }

  private syncMusic(unlocked: boolean) {
    if (!this.lofi) {
      return;
    }

    if (!unlocked) {
      this.lofi.stop();
      this.setMusicVolume(MAIN_MUSIC_VOLUME);
      return;
    }

    if (!this.lofi.isPlaying) {
      this.lofi.play();
    }

    this.setMusicVolume(MAIN_MUSIC_VOLUME);
  }

  private duckMusic(targetVolume: number, durationMs: number) {
    if (!this.lofi || !this.lofi.isPlaying) {
      return;
    }

    this.restoreMusicTimer?.remove(false);
    this.restoreMusicTimer = undefined;

    this.setMusicVolume(Math.min(MAIN_MUSIC_VOLUME, targetVolume));
    this.restoreMusicTimer = this.scene.time.delayedCall(durationMs, () => {
      if (this.lofi && this.lofi.isPlaying) {
        this.setMusicVolume(MAIN_MUSIC_VOLUME);
      }
      this.restoreMusicTimer = undefined;
    });
  }

  private playOneShot(key: string, volume: number) {
    if (!this.scene.cache.audio.exists(key)) {
      return;
    }

    this.scene.sound.play(key, {
      volume,
    });
  }

  private randomLikeVolume() {
    const volumes = [0.1, 0.05, 0.025];
    return volumes[Math.floor(Math.random() * volumes.length)];
  }

  private getNextGenericPowerupSoundKey() {
    if (POWERUP_PURCHASE_SOUND_KEYS.length === 1) {
      return POWERUP_PURCHASE_SOUND_KEYS[0];
    }

    const availableKeys = POWERUP_PURCHASE_SOUND_KEYS.filter(
      (key) => key !== this.lastPowerupSoundKey,
    );
    const nextKey =
      availableKeys[Math.floor(Math.random() * availableKeys.length)];

    this.lastPowerupSoundKey = nextKey;
    return nextKey;
  }

  private setMusicVolume(volume: number) {
    (this.lofi as VolumeControlledSound | undefined)?.setVolume(volume);
  }
}
